import React from "react";
import styled from "styled-components";
import { SkeletonBox, SkeletonText, SkeletonCard, SkeletonButton } from "./SkeletonBox";

/**
 * HomePageSkeleton - Non-animated skeleton for TV Home page
 * 
 * Matches layout of:
 * - topSlider.jsx (Hero: 80vh, 80vw)
 * - moviesList.jsx (Cards: 250x350, rows with 60px left padding)
 * - content.jsx (Scrollable rows)
 */

var HeroSkeleton = styled.div`
  width: 100%;
  height: 80vh;
  position: relative;
  background-color: #151515;
  margin-bottom: 5rem;
  
  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 400px;
    background: -webkit-linear-gradient(bottom, #121212, rgba(0, 0, 0, 0));
    background: -moz-linear-gradient(bottom, #121212, rgba(0, 0, 0, 0));
    background: linear-gradient(to top, #121212, rgba(0, 0, 0, 0));
  }
`;

var HeroContent = styled.div`
  position: absolute;
  bottom: 10rem;
  right: 2rem;
  left: 2rem;
  z-index: 1;
`;

var ContentRowWrapper = styled.div`
  position: relative;
  min-height: 400px;
  margin-top: 2rem;
  margin-bottom: 2rem;
  
  &::after {
    content: "";
    position: absolute;
    width: 312px;
    height: 100%;
    background: -webkit-linear-gradient(left, #121212, rgba(0, 0, 0, 0));
    background: -moz-linear-gradient(left, #121212, rgba(0, 0, 0, 0));
    background: linear-gradient(to right, #121212, rgba(0, 0, 0, 0));
    left: 0;
    top: 0;
    pointer-events: none;
  }
`;

var RowTitle = styled.div`
  height: 27px;
  width: 200px;
  margin-bottom: 22px;
  margin-right: 40px;
  padding-top: 40px;
  background-color: #262626;
  -webkit-border-radius: 4px;
  -moz-border-radius: 4px;
  border-radius: 4px;
`;

var CardsContainer = styled.div`
  display: -webkit-box;
  display: -webkit-flex;
  display: -moz-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-orient: horizontal;
  -webkit-box-direction: normal;
  -webkit-flex-direction: row;
  -moz-box-orient: horizontal;
  -moz-box-direction: normal;
  -ms-flex-direction: row;
  flex-direction: row;
  padding-left: 60px;
  overflow: hidden;
`;

var CardWrapper = styled.div`
  margin-left: 12px;
  -webkit-flex-shrink: 0;
  -moz-flex-shrink: 0;
  -ms-flex-negative: 0;
  flex-shrink: 0;
  
  &:last-child {
    margin-left: 0;
  }
`;

var MetadataRow = styled.div`
  display: -webkit-box;
  display: -webkit-flex;
  display: -moz-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-orient: horizontal;
  -webkit-box-direction: normal;
  -webkit-flex-direction: row;
  -moz-box-orient: horizontal;
  -moz-box-direction: normal;
  -ms-flex-direction: row;
  flex-direction: row;
  -webkit-box-align: center;
  -webkit-align-items: center;
  -moz-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  margin-top: 16px;
`;

var MetadataItem = styled.div`
  margin-left: 16px;
  
  &:last-child {
    margin-left: 0;
  }
`;

var ButtonsRow = styled.div`
  display: -webkit-box;
  display: -webkit-flex;
  display: -moz-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-orient: horizontal;
  -webkit-box-direction: normal;
  -webkit-flex-direction: row;
  -moz-box-orient: horizontal;
  -moz-box-direction: normal;
  -ms-flex-direction: row;
  flex-direction: row;
  -webkit-box-align: center;
  -webkit-align-items: center;
  -moz-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  margin-top: 24px;
`;

var ButtonWrapper = styled.div`
  margin-left: 12px;
  
  &:last-child {
    margin-left: 0;
  }
`;

/**
 * Hero Section Skeleton
 */
function HeroSectionSkeleton() {
  return (
    <HeroSkeleton>
      <HeroContent>
        {/* Title */}
        <SkeletonText width={400} size="xl" style={{ marginBottom: 16 }} />

        {/* English title */}
        <MetadataRow>
          <MetadataItem>
            <SkeletonText width={250} size="lg" />
          </MetadataItem>
        </MetadataRow>

        {/* Metadata: Year | Duration | Rating */}
        <MetadataRow>
          <MetadataItem>
            <SkeletonText width={80} size="md" />
          </MetadataItem>
          <MetadataItem>
            <SkeletonBox width={2} height={20} borderRadius={1} />
          </MetadataItem>
          <MetadataItem>
            <SkeletonText width={100} size="md" />
          </MetadataItem>
        </MetadataRow>

        {/* Watch Button */}
        <ButtonsRow>
          <ButtonWrapper>
            <SkeletonButton variant="primary" />
          </ButtonWrapper>
        </ButtonsRow>
      </HeroContent>
    </HeroSkeleton>
  );
}

// ============================================================================
// TURKYLAR ROW SKELETON
// Matches turkylar.jsx: 306x170 landscape cards, semi-transparent bg, -7rem translate
// ============================================================================

var TurkylarWrapper = styled.div`
  margin: 0 2rem;
  position: relative;
  background: rgba(255, 255, 255, 0.06);
  -webkit-border-radius: 20px;
  -moz-border-radius: 20px;
  border-radius: 20px;
  -webkit-transform: translateY(-7rem);
  -moz-transform: translateY(-7rem);
  -ms-transform: translateY(-7rem);
  transform: translateY(-7rem);
`;

var TurkylarTitle = styled.div`
  height: 27px;
  width: 200px;
  margin-bottom: 10px;
  padding-right: 20px;
  padding-top: 20px;
  margin-right: 20px;
  background-color: #262626;
  -webkit-border-radius: 4px;
  -moz-border-radius: 4px;
  border-radius: 4px;
`;

var TurkylarCardsContainer = styled.div`
  display: -webkit-box;
  display: -webkit-flex;
  display: -moz-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-orient: horizontal;
  -webkit-box-direction: normal;
  -webkit-flex-direction: row;
  -moz-box-orient: horizontal;
  -moz-box-direction: normal;
  -ms-flex-direction: row;
  flex-direction: row;
  padding: 20px;
  padding-left: 0;
  overflow: hidden;
`;

var TurkylarCardWrapper = styled.div`
  margin-left: 2rem;
  -webkit-flex-shrink: 0;
  -moz-flex-shrink: 0;
  -ms-flex-negative: 0;
  flex-shrink: 0;

  &:last-child {
    margin-left: 0;
  }
`;

/**
 * Turkylar Row Skeleton
 * Matches turkylar.jsx: landscape cards 306x170 with semi-transparent background
 */
function TurkylarRowSkeleton({ cardCount = 5 }) {
  var cards = [];
  for (var i = 0; i < cardCount; i++) {
    cards.push(
      <TurkylarCardWrapper key={"tlr_" + i}>
        <SkeletonBox width={306} height={170} borderRadius={10} />
      </TurkylarCardWrapper>
    );
  }

  return (
    <TurkylarWrapper>
      <TurkylarTitle />
      <TurkylarCardsContainer>
        {cards}
      </TurkylarCardsContainer>
    </TurkylarWrapper>
  );
}

/**
 * Content Row Skeleton
 */
function ContentRowSkeleton({ cardType = "portrait", cardCount = 6 }) {
  var cards = [];
  for (var i = 0; i < cardCount; i++) {
    cards.push(
      <CardWrapper key={"card_" + i}>
        <SkeletonCard type={cardType} />
      </CardWrapper>
    );
  }

  return (
    <ContentRowWrapper>
      <RowTitle />
      <CardsContainer>
        {cards}
      </CardsContainer>
    </ContentRowWrapper>
  );
}

/**
 * HomePageSkeleton - Main export
 * Default: Hero + Turkylar + 1 MovieSlider row (matching real home page structure)
 */
function HomePageSkeleton({ showHero = true, rowCount = 1 }) {
  var rows = [];
  for (var i = 0; i < rowCount; i++) {
    rows.push(
      <ContentRowSkeleton
        key={"row_" + i}
        cardType="portrait"
        cardCount={6}
      />
    );
  }

  return (
    <div>
      {showHero && <HeroSectionSkeleton />}
      {showHero && <TurkylarRowSkeleton />}
      {rows}
    </div>
  );
}

export { HeroSectionSkeleton, ContentRowSkeleton, TurkylarRowSkeleton };
export default HomePageSkeleton;

